import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatCutoffWhen,
  formatMoney,
  formatTimeLeft,
  formatTimeLeftShort,
  isOpen,
  type AppNotification,
  type DayMenu,
  type DeliveryPoint,
  type MenuOffer,
  type MonthlySummary,
  type MyOrder,
  type Notice,
} from '@mealdesk/shared';
import { apiFetch } from './api.js';

/**
 * The single seam between the screens and the ordering API.
 *
 * Every screen reads through here, so where the data comes from is one
 * module's business. That is what let the design run ahead of the endpoints:
 * the pages never knew they were reading fixtures, and they did not change
 * when this started calling the server.
 */

/** The date in the phone's own calendar. `toISOString` would be UTC's. */
export function todayIso(): string {
  return new Date().toLocaleDateString('en-CA');
}

const OFFERS = ['orders', 'offers'] as const;
const MINE = ['orders', 'mine'] as const;
const PROFILE = ['me', 'profile'] as const;

export type Profile = {
  id: string;
  name: string;
  departmentName: string | null;
  defaultPointId: string | null;
  pointName: string | null;
  siteId: string | null;
  siteName: string | null;
  timeZone: string | null;
  /** Whether this organisation shows its people what a meal costs. */
  showBilling: boolean;
  deliveryPoints: Array<DeliveryPoint & { siteName: string }>;
};

export function useProfile(): Profile | undefined {
  const { data } = useQuery({
    queryKey: PROFILE,
    queryFn: () => apiFetch<Profile>('/api/me/profile'),
    staleTime: 5 * 60_000,
  });
  return data;
}

function useOffers(): { days: DayMenu[]; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: OFFERS,
    queryFn: () => apiFetch<{ items: MenuOffer[] }>('/api/orders/offers'),
  });

  const days = useMemo(() => {
    const byDate = new Map<string, MenuOffer[]>();
    for (const offer of data?.items ?? []) {
      const found = byDate.get(offer.serviceDate);
      if (found) found.push(offer);
      else byDate.set(offer.serviceDate, [offer]);
    }
    return [...byDate.entries()]
      .map(([serviceDate, offers]) => ({ serviceDate, offers }))
      .sort((a, b) => a.serviceDate.localeCompare(b.serviceDate));
  }, [data]);

  return { days, isLoading };
}

/**
 * The days that can still be ordered for, soonest first.
 *
 * Most kitchens close the day before, so on an ordinary morning this starts at
 * tomorrow and today is not in it at all. The home screen is built on this
 * rather than on the calendar date, because a screen that leads with today
 * leads with the one day nobody can act on.
 */
export function useOpenDays(): DayMenu[] {
  const { days } = useOffers();
  return useMemo(
    () =>
      days
        .map((day) => ({ ...day, offers: day.offers.filter((o) => isOpen(o.cutoffAt)) }))
        .filter((day) => day.offers.length > 0),
    [days],
  );
}

/** Tomorrow onward — the days an employee can still act on. */
export function useWeek(): DayMenu[] {
  const { days } = useOffers();
  const date = todayIso();
  return useMemo(() => days.filter((d) => d.serviceDate > date), [days, date]);
}

export function useOffer(
  serviceDate: string | undefined,
  mealType: string | undefined,
  menuItemId: string | undefined,
): { offer: MenuOffer | undefined; isLoading: boolean } {
  const { days, isLoading } = useOffers();
  const offer = days
    .find((d) => d.serviceDate === serviceDate)
    ?.offers.find((o) => o.id === menuItemId && o.mealType === mealType);
  return { offer, isLoading };
}

export function useDeliveryPoints(): DeliveryPoint[] {
  return useProfile()?.deliveryPoints ?? [];
}

export function useNotice(): Notice | undefined {
  const { data } = useQuery({
    queryKey: ['notices', 'active'],
    queryFn: () => apiFetch<{ items: Notice[] }>('/api/notices/active'),
    staleTime: 5 * 60_000,
  });
  return data?.items[0];
}

export function useNotifications(): { items: AppNotification[]; unread: number } {
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => apiFetch<{ items: AppNotification[]; unread: number }>('/api/notifications'),
  });
  return { items: data?.items ?? [], unread: data?.unread ?? 0 };
}

/** Marks the feed read on the way out, so the badge clears when it is seen. */
export function useMarkNotificationsRead(): () => void {
  const client = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: () => apiFetch<{ read: number }>('/api/notifications/read-all', { method: 'POST' }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['notifications'] }),
  });
  return mutate;
}

export function useMonthly(month?: string): MonthlySummary {
  const { data } = useQuery({
    queryKey: ['orders', 'summary', month ?? 'current'],
    queryFn: () =>
      apiFetch<MonthlySummary>(`/api/orders/summary${month ? `?month=${month}` : ''}`),
  });

  return (
    data ?? {
      month: month ?? todayIso().slice(0, 7),
      currency: 'USD',
      total: 0,
      mealCount: 0,
      byRestaurant: [],
    }
  );
}

/** Orders still ahead of you, soonest first — what somebody booked in advance. */
export function useUpcoming(): MyOrder[] {
  const orders = useHistory();
  const date = todayIso();
  return useMemo(
    () =>
      orders
        .filter(
          (o) =>
            o.serviceDate > date &&
            o.status !== 'cancelled' &&
            o.status !== 'cancelled_exception',
        )
        .sort((a, b) => a.serviceDate.localeCompare(b.serviceDate)),
    [orders, date],
  );
}

/** What you are collecting today, which is the only use today still has. */
export function useTodayOrders(): MyOrder[] {
  const orders = useHistory();
  const date = todayIso();
  return useMemo(
    () =>
      orders.filter(
        (o) =>
          o.serviceDate === date &&
          o.status !== 'cancelled' &&
          o.status !== 'cancelled_exception',
      ),
    [orders, date],
  );
}

export function useHistory(): MyOrder[] {
  const { data } = useQuery({
    queryKey: MINE,
    queryFn: () => apiFetch<{ items: MyOrder[] }>('/api/orders/mine'),
  });
  return data?.items ?? [];
}

/** The order covering a given date and meal, if this person has one. */
export function useOrderFor(serviceDate: string, mealType: string): MyOrder | undefined {
  return useHistory().find(
    (o) =>
      o.serviceDate === serviceDate &&
      o.mealType === mealType &&
      o.status !== 'cancelled' &&
      o.status !== 'cancelled_exception',
  );
}

export function useOrder(orderId: string | undefined): MyOrder | undefined {
  const orders = useHistory();
  if (!orderId) return undefined;
  return orders.find((o) => o.id === orderId);
}

/**
 * Places an order and hands back its id.
 *
 * Replacing an existing order for the same meal is the server's business, not
 * a decision made here: only it knows whether the deadline has actually passed
 * (§3.3). The screens ask, and take the answer.
 */
export function usePlaceOrder() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: { offer: MenuOffer; deliveryPointId: string }) =>
      apiFetch<{ id: string; replaced: boolean }>('/api/orders', {
        method: 'POST',
        body: {
          menuItemId: input.offer.id,
          serviceDate: input.offer.serviceDate,
          mealType: input.offer.mealType,
          deliveryPointId: input.deliveryPointId || undefined,
        },
      }),
    onSuccess: async () => {
      // Both: an order changes what is left of a capped meal.
      await client.invalidateQueries({ queryKey: MINE });
      await client.invalidateQueries({ queryKey: OFFERS });
    },
  });
}

export type BatchResult = {
  items: Array<{
    serviceDate: string;
    mealType: string;
    placed: boolean;
    id?: string;
    replaced?: boolean;
    message?: string;
  }>;
  placed: number;
};

/**
 * Books several days at once.
 *
 * The server places each meal on its own and reports per day, so a week where
 * one day is full still gets the other four ordered. The screen reads that
 * list rather than assuming it all worked.
 */
export function usePlaceOrders() {
  const client = useQueryClient();

  return useMutation({
    // A pickup point per meal, not per batch: the day somebody is on another
    // line is the day a single point for the week loses their lunch.
    mutationFn: (input: { items: Array<{ offer: MenuOffer; deliveryPointId?: string }> }) =>
      apiFetch<BatchResult>('/api/orders/batch', {
        method: 'POST',
        body: {
          items: input.items.map(({ offer, deliveryPointId }) => ({
            menuItemId: offer.id,
            serviceDate: offer.serviceDate,
            mealType: offer.mealType,
            deliveryPointId: deliveryPointId || undefined,
          })),
        },
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: MINE });
      await client.invalidateQueries({ queryKey: OFFERS });
    },
  });
}

export function useCancelOrder() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (orderId: string) =>
      apiFetch<{ cancelled: boolean }>(`/api/orders/${orderId}/cancel`, { method: 'POST' }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: MINE });
      await client.invalidateQueries({ queryKey: OFFERS });
    },
  });
}

/**
 * Ticks once a second so the countdown stays honest while the screen is open,
 * and hands back the three ways the screens need to say it: how long is left,
 * the same in two characters for the middle of the ring, and when it shuts.
 *
 * Null once the slot has closed, which is the signal to swap the action button
 * for a closed state rather than letting a stale timer linger.
 */
export function useTimeLeft(
  cutoffAt: string | undefined,
): { long: string; short: string; when: string } | null {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!cutoffAt) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [cutoffAt]);

  if (!cutoffAt || !isOpen(cutoffAt, now)) return null;

  return {
    long: formatTimeLeft(cutoffAt, now),
    short: formatTimeLeftShort(cutoffAt, now),
    when: formatCutoffWhen(cutoffAt, now),
  };
}

/**
 * The money on a card, as much of it as this organisation discloses.
 *
 * Returns null when there is nothing the employee is allowed to be told and
 * nothing they owe — the screens then print no money at all rather than a
 * zero, because a zero invites the question this setting exists to avoid.
 */
export function useMoney(): (
  price: number,
  employeeShare: number,
  currency: string,
) => string | null {
  const show = useProfile()?.showBilling ?? true;

  return (price, employeeShare, currency) => {
    if (show) return formatMoney(price, currency);
    if (employeeShare > 0) return formatMoney(employeeShare, currency);
    return null;
  };
}

/** One meal on one day: the key the batch screens carry around. */
export function offerKey(offer: Pick<MenuOffer, 'id' | 'serviceDate' | 'mealType'>): string {
  return `${offer.serviceDate}|${offer.mealType}|${offer.id}`;
}
